/**
 * Conversation ID storage for Word add-in.
 *
 * Stores the current conversation ID in localStorage so that when the
 * add-in is reloaded, it can restore the user to their previous conversation
 * instead of starting a new one.
 *
 * Storage is keyed by workspace ID to support multiple workspaces.
 */

const STORAGE_KEY_PREFIX = "walter_word_conversation_";

/**
 * Get the localStorage key for a workspace.
 */
function getStorageKey(workspaceId: string): string {
  return `${STORAGE_KEY_PREFIX}${workspaceId}`;
}

/**
 * Store the current conversation ID for a workspace.
 *
 * @param workspaceId - The workspace ID
 * @param conversationId - The conversation ID to store (or null to clear)
 */
export function storeConversationId(workspaceId: string, conversationId: string | null): void {
  const key = getStorageKey(workspaceId);

  try {
    if (conversationId) {
      localStorage.setItem(key, conversationId);
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    // localStorage may be unavailable in some contexts
    console.warn("[ConversationStorage] Failed to store conversation ID:", error);
  }
}

/**
 * Get the stored conversation ID for a workspace.
 *
 * @param workspaceId - The workspace ID
 * @returns The stored conversation ID, or null if none
 */
export function getStoredConversationId(workspaceId: string): string | null {
  const key = getStorageKey(workspaceId);

  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn("[ConversationStorage] Failed to get conversation ID:", error);
    return null;
  }
}

/**
 * Clear the stored conversation ID for a workspace.
 *
 * @param workspaceId - The workspace ID
 */
export function clearStoredConversationId(workspaceId: string): void {
  storeConversationId(workspaceId, null);
}
